import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { C } from "../../../common/theme";
import { useIsNarrow } from "../../../ui";
import { RANGE_OPTIONS, RangeKey, ResolvedRange, SalesFilterState, activeFilterCount, toDateInput } from "../salesData";

export interface Option {
  value: string;
  label: string;
}

export interface FilterOptions {
  statuses: Option[];
  sellers: Option[];
  buyers: Option[];
  currencies: Option[];
  countries: Option[];
  genera: Option[];
}

interface SalesFiltersProps {
  filters: SalesFilterState;
  range: ResolvedRange;
  options: FilterOptions;
  showIdentity: boolean;
  onChange: (next: Partial<SalesFilterState>) => void;
  onClear: () => void;
  /** Items matching the filters, shown on the phone dialog's Done button */
  resultCount: number;
}

function MultiFilter({
  label,
  options,
  value,
  onChange,
  width = 180,
  full,
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (v: string[]) => void;
  width?: number;
  full?: boolean;
}) {
  const selected = options.filter((o) => value.includes(o.value));
  // Keep values that came from the URL but aren't in the data any more visible, so they can be removed.
  const missing = value.filter((v) => !options.some((o) => o.value === v)).map((v) => ({ value: v, label: v }));
  return (
    <Autocomplete
      multiple
      size="small"
      limitTags={full ? 3 : 1}
      options={[...options, ...missing]}
      value={[...selected, ...missing]}
      onChange={(_, v) => onChange(v.map((o) => o.value))}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      disableCloseOnSelect
      renderInput={(params) => <TextField {...params} label={label} />}
      slotProps={{ chip: { size: "small" } }}
      sx={{ width: full ? "100%" : width }}
    />
  );
}

export default function SalesFilters({ filters, range, options, showIdentity, onChange, onClear, resultCount }: SalesFiltersProps) {
  const narrow = useIsNarrow(760);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  // The search box writes to the URL on every keystroke otherwise; debounce it.
  const [query, setQuery] = React.useState(filters.q);
  React.useEffect(() => {
    setQuery(filters.q);
  }, [filters.q]);
  React.useEffect(() => {
    if (query === filters.q) return;
    const t = setTimeout(() => onChange({ q: query }), 250);
    return () => clearTimeout(t);
  }, [query, filters.q, onChange]);

  const handleRange = (key: RangeKey) => {
    if (key === "custom") {
      onChange({
        range: key,
        from: filters.from ?? (range.start ? toDateInput(range.start) : undefined),
        to: filters.to ?? toDateInput(new Date(range.end.getTime() - 1)),
      });
    } else {
      onChange({ range: key, from: undefined, to: undefined });
    }
  };

  const active = activeFilterCount(filters);
  // The search box stays outside the phone dialog, so it isn't counted on the button.
  const dialogCount = active - (filters.q.trim() ? 1 : 0) + (filters.range !== "all" ? 1 : 0);

  const search = (
    <TextField
      size="small"
      placeholder="Search order, listing, user, tracking…"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      inputProps={{ "aria-label": "Search orders" }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
      sx={{ flex: "1 1 220px", minWidth: 0 }}
    />
  );

  const dateFields = (full: boolean) => (
    <>
      <TextField
        select
        size="small"
        label="Date range"
        value={filters.range}
        onChange={(e) => handleRange(e.target.value as RangeKey)}
        sx={{ width: full ? "100%" : 170 }}
      >
        {RANGE_OPTIONS.map((r) => (
          <MenuItem key={r.key} value={r.key}>
            {r.label}
          </MenuItem>
        ))}
      </TextField>
      {filters.range === "custom" && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, width: full ? "100%" : 316 }}>
          <TextField
            type="date"
            size="small"
            label="From"
            value={filters.from ?? ""}
            onChange={(e) => onChange({ from: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={filters.to ?? ""}
            onChange={(e) => onChange({ to: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      )}
    </>
  );

  const dimensionFields = (full: boolean) => (
    <>
      <MultiFilter full={full} label="Status" options={options.statuses} value={filters.statuses} onChange={(v) => onChange({ statuses: v })} width={200} />
      {showIdentity && (
        <>
          <MultiFilter full={full} label="Seller" options={options.sellers} value={filters.sellers} onChange={(v) => onChange({ sellers: v })} />
          <MultiFilter full={full} label="Buyer" options={options.buyers} value={filters.buyers} onChange={(v) => onChange({ buyers: v })} />
        </>
      )}
      <MultiFilter full={full} label="Genus" options={options.genera} value={filters.genera} onChange={(v) => onChange({ genera: v })} width={160} />
      <MultiFilter full={full} label="Ships to" options={options.countries} value={filters.countries} onChange={(v) => onChange({ countries: v })} width={160} />
      <MultiFilter full={full} label="Currency" options={options.currencies} value={filters.currencies} onChange={(v) => onChange({ currencies: v })} width={140} />
      <Chip
        icon={<WarningAmberIcon />}
        label="Needs attention"
        color={filters.attention ? "warning" : "default"}
        variant={filters.attention ? "filled" : "outlined"}
        onClick={() => onChange({ attention: !filters.attention })}
        aria-pressed={filters.attention}
        sx={{ height: full ? 44 : 36, alignSelf: full ? "flex-start" : "center" }}
      />
    </>
  );

  if (narrow) {
    return (
      <>
        <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
          {search}
          <Button
            variant={dialogCount > 0 ? "contained" : "outlined"}
            startIcon={<TuneIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ flex: "0 0 auto", minHeight: 44 }}
          >
            Filters{dialogCount > 0 ? ` (${dialogCount})` : ""}
          </Button>
        </Box>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullScreen>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              pt: "calc(12px + env(safe-area-inset-top, 0px))",
              pb: 1.5,
              borderBottom: `1px solid ${C.line}`,
            }}
          >
            <Typography variant="h5" component="h2" sx={{ flex: 1 }}>
              Filters
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} aria-label="Close" sx={{ width: 44, height: 44, mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {dateFields(true)}
            {dimensionFields(true)}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              px: 2,
              pt: 1.5,
              pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
              borderTop: `1px solid ${C.line}`,
              "& > *": { flex: 1, minHeight: 44 },
            }}
          >
            <Button variant="outlined" onClick={onClear} disabled={active === 0}>
              Clear
            </Button>
            <Button variant="contained" onClick={() => setDialogOpen(false)}>
              Show {resultCount} item{resultCount === 1 ? "" : "s"}
            </Button>
          </Box>
        </Dialog>
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {dateFields(false)}
        {search}
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {dimensionFields(false)}
        {active > 0 && (
          <Button size="small" onClick={onClear}>
            Clear {active} filter{active > 1 ? "s" : ""}
          </Button>
        )}
      </Box>
    </Box>
  );
}
